import React, { useMemo, useState } from "react";
import { Building2, FileSpreadsheet, MapPin, Route } from "lucide-react";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { TspBreadcrumb, TspEmptyState } from "../../components/primitives";
import TspLoadError from "../../components/common/TspLoadError";
import useTspApiResource from "../../hooks/useTspApiResource";
import TspApiService from "../../api/tspApiService";
import notification from "../../../../helpers/notification";
import { useTspTempleStatsData } from "./useTspStatsData";
import StatCardsRow from "../dashboard/sections/StatCardsRow";
import BookDistributionOverview from "../dashboard/sections/BookDistributionOverview";
import DistributionByMethod from "../dashboard/sections/DistributionByMethod";
import VisitWiseChart from "../dashboard/sections/VisitWiseChart";
import BookWiseChart from "../dashboard/sections/BookWiseChart";
import YearWiseBookDistributionChart from "../dashboard/sections/YearWiseBookDistributionChart";
import * as model from "../dashboard/dashboardModel";
import { formatNumber } from "../../utils/tspFormat";
import DistributionSummarySection from "./components/DistributionSummarySection";
import PublicResponseSummarySection from "./components/PublicResponseSummarySection";
import LogisticsSummarySection from "./components/LogisticsSummarySection";
import MediaGallerySection from "./components/MediaGallerySection";
import PastJourneysTable, { PastJourneysTitle } from "../journeys/components/PastJourneysTable";
import downloadJourneysExport, { exportErrorMessage } from "../journeys/downloadJourneysExport";

// "Your Stats" (/tsp/stats): Overall aggregate summary across ALL journeys
// created for the signed-in user's assigned temple(s).
export default function TspTempleStatsView() {
  const { data, loading, error, reload } = useTspTempleStatsData();
  const [journeyPage, setJourneyPage] = useState(1);
  const [exporting, setExporting] = useState(false);

  const temples = data?.temples || [];
  const templeText = temples.join(", ");

  const templePlacesQuery = useMemo(
    () => ({ temple: templeText, page: journeyPage, limit: 10 }),
    [templeText, journeyPage]
  );

  const placesResource = useTspApiResource(
    () => (templeText ? TspApiService.listJourneyPlaces(templePlacesQuery) : Promise.resolve({ data: [], meta: { page: 1, limit: 10, total: 0, totalPages: 1 } })),
    [templeText, journeyPage]
  );

  const handleExport = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      await downloadJourneysExport({ temple: templeText, status: "all" });
    } catch (err) {
      notification("error", await exportErrorMessage(err));
    } finally {
      setExporting(false);
    }
  };

  if (error) return <TspLoadError error={error} onRetry={reload} what="your temple's stats" />;
  if (loading || !data) return <div className="tsp-page tsp-dashboard-loading">Loading stats…</div>;

  const heading = (
    <div className="tsp-page-heading-row">
      <div>
        <h1>Your Stats</h1>
        <TspBreadcrumb items={["Dashboard", "Your Stats"]} />
      </div>
      {temples.length > 0 && (
        <span className="tsp-temple-stats-temple">
          <Building2 size={15} aria-hidden="true" /> {templeText}
        </span>
      )}
    </div>
  );

  if (temples.length === 0) {
    return (
      <div className="tsp-page tsp-stats-page">
        {heading}
        <TspEmptyState
          icon={Building2}
          title="No temple on your account"
          message="Your Stats shows the journeys of your temple. Ask an administrator to set your temple in the Admin Panel."
        />
      </div>
    );
  }

  const hasJourneys =
    data.stats &&
    (Number(data.stats.total_places) > 0 ||
      Number(data.stats.books_distributed) > 0 ||
      Number(data.stats.active_journeys) > 0 ||
      Number(data.stats.cities_covered) > 0);

  if (!hasJourneys) {
    return (
      <div className="tsp-page tsp-stats-page">
        {heading}
        <TspEmptyState
          icon={MapPin}
          title="No Journeys available for your temple."
          message={`No journey has been added for ${templeText} yet. Its stats appear here as soon as a journey is created.`}
        />
      </div>
    );
  }

  const statCards = model.buildStatCards(data.stats);
  const distributionOverview = model.buildBookDistributionOverview(data.distribution_overview, data.stats);
  const distributionByMethod = model.buildDistributionByMethod(data.by_method || []);
  const visitWise = model.buildVisitWise(data.visit_wise);
  const bookWise = model.buildBookWiseChart(data.book_wise);
  const yearWise = model.buildYearWiseDistribution(data.year_wise);

  const renderPlacesList = () => {
    if (placesResource.error) {
      return <TspLoadError error={placesResource.error} onRetry={placesResource.reload} what="journey places" />;
    }
    if (placesResource.loading && !placesResource.data) {
      return <div className="tsp-page tsp-dashboard-loading">Loading journey places…</div>;
    }
    if (!placesResource.data || placesResource.data.length === 0) {
      return (
        <TspEmptyState
          icon={Route}
          title="No journeys found"
          message={`No journey places match ${templeText}.`}
        />
      );
    }
    return (
      <PastJourneysTable
        rows={placesResource.data}
        meta={placesResource.meta || { page: 1, limit: 10, total: placesResource.data.length, totalPages: 1 }}
        onPageChange={setJourneyPage}
      />
    );
  };

  const totals = data.totals || { completed: 0, cancelled: 0 };
  const nothingToExport = !placesResource.data || placesResource.data.length === 0;

  return (
    <div className="tsp-page tsp-stats-page tsp-temple-stats-page">
      {heading}

      {/* Top Section: Overview KPI Cards & Charts from the Temple */}
      <StatCardsRow stats={statCards} />

      <div className="tsp-grid tsp-grid-2-1">
        <BookDistributionOverview overview={distributionOverview} />
        <DistributionByMethod data={distributionByMethod} total={formatNumber(data.stats.books_distributed)} />
      </div>

      <div className="tsp-grid tsp-grid-3 tsp-dashboard-charts">
        <VisitWiseChart data={visitWise} />
        <BookWiseChart data={bookWise.data} series={bookWise.series} />
        <YearWiseBookDistributionChart data={yearWise} />
      </div>

      {/* Temple All Journeys List: directly above Distribution Summary */}
      <div className="tsp-journey-list" id="temple-journeys-list">
        <div className="tsp-journey-list-head">
          <PastJourneysTitle title="All Journeys" totals={placesResource.meta?.totals || totals} />
          <button
            type="button"
            className="tsp-btn tsp-btn-outline-success tsp-btn-sm tsp-journey-export-btn"
            onClick={handleExport}
            disabled={exporting || nothingToExport}
            title="Download every journey of this temple as an Excel file"
            aria-label={exporting ? "Exporting…" : "Export to Excel"}
          >
            <FileSpreadsheet size={15} aria-hidden="true" />
            <span className="tsp-journey-export-text">{exporting ? "Exporting…" : "Export to Excel"}</span>
          </button>
        </div>
        {renderPlacesList()}
      </div>

      {/* Bottom Section: Overall Detailed Summaries for all journeys of this temple */}
      <div id="distribution-summary">
        <DistributionSummarySection summary={data.distribution_summary} subject={templeText} />
      </div>
      <div id="public-response">
        <PublicResponseSummarySection response={data.public_response} cities={data.cities} subject={templeText} />
      </div>
      <div id="logistics">
        <LogisticsSummarySection logistics={data.logistics} subject={templeText} />
      </div>
      <div id="media-gallery">
        <MediaGallerySection gallery={data.media_gallery} subject={templeText} />
      </div>
      <ToastContainer />
    </div>
  );
}

